/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useCallback, useState } from 'react';
import { Grid } from '@/components/Grid';
import { useLabels } from '@/hooks/useLabels';
import { VINDecoderForm } from '@/features/ymme/components/VINDecoderForm';
import type { VehicleProps } from '@/features/ymme';
import VehicleCard from './VehicleCard';
import { labelMap } from './labels';
import styles from './styles.module.scss';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useVehiclesMutationACES } from '@/features/ymme/api/postVehiclesACES';
import { setYmmeActionSource } from '@/features/ymme/utils/ymmeActionSourceHelper';
import { ymmeActionSources } from '@/features/ymme/utils/ymmeHelpers';

export const VinDecoder = () => {
  const labels = useLabels(labelMap);
  const [vehiclesByVin, setVehiclesByVin] = useState<VehicleProps[]>([]);
  const showVehicles = vehiclesByVin.length;
  const { acesEnabled } = useAcesFlags();
  const v2VehicleDecoderEnabled = useForteDecisionFlagWithTrueDefault('v2VehicleDecoderEnabled');
  const { mutate: ymmeAddVehicleACES } = useVehiclesMutationACES();

  const onVinLookupSuccess = useCallback(
    (results?: VehicleProps[]) => {
      if (!results) {
        return;
      }
      if (v2VehicleDecoderEnabled) {
        if (results.length === 1) {
          const acesVehicleAttributes = results[0].acesVehicleAttributes;
          if (!acesVehicleAttributes) {
            return;
          }
          setVehiclesByVin([]);
          setYmmeActionSource(ymmeActionSources.vinLookup);
          ymmeAddVehicleACES({
            acesVehicleAttributes: { ...acesVehicleAttributes, vin: results[0].vin },
          });
        } else if (results.length > 1) {
          setVehiclesByVin(results);
        }
      } else {
        setVehiclesByVin(results);
      }
    },
    [v2VehicleDecoderEnabled, ymmeAddVehicleACES],
  );

  return (
    <div id="vin-code">
      <VINDecoderForm
        onVinLookupSuccess={onVinLookupSuccess}
        buttonText={acesEnabled ? labels.lblAddVin : undefined}
        interactionLocation="VIN-Decoder"
      />
      {showVehicles ? (
        <Grid item lg={12} xs={12} md={12} className={styles.vehiclesListContainer}>
          <div className={styles.infoText}>
            {vehiclesByVin.length === 1
              ? labels.vinDecoderOneMatch
              : vehiclesByVin.length > 1
                ? labels.vinDecoderMultipleMatches
                : null}
          </div>
          {vehiclesByVin.map((vehicle: VehicleProps) => (
            <VehicleCard
              vehicle={vehicle}
              key={vehicle.vehicleId}
              setVehiclesByVin={setVehiclesByVin}
            />
          ))}
        </Grid>
      ) : null}
    </div>
  );
};

export default VinDecoder;
